import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

/**
 * Custom hook to fetch organization activities with filters
 * @param {string} orgId - Organization ID
 * @param {Object} filters - Filter options (category, userId, entityType, entityId, limit, offset)
 * @param {boolean} autoFetch - Whether to auto-fetch on mount (default: true)
 * @returns {Object} - { activities, total, loading, error, refetch }
 */
export const useOrganizationActivities = (orgId, filters = {}, autoFetch = true) => {
  const [activities, setActivities] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(autoFetch);
  const [error, setError] = useState(null);
  const { getAccessTokenSilently } = useAuth0();

  const isMountedRef = useRef(true);

  const fetchActivities = useCallback(async () => {
    if (!orgId) {
      setError('Organization ID is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get Auth0 token
      const token = await getAccessTokenSilently();

      // Build query params
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.entityType) params.append('entityType', filters.entityType);
      if (filters.entityId) params.append('entityId', filters.entityId);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.offset) params.append('offset', filters.offset);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${orgId}/activities?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch activities');
      }

      const data = await response.json();

      if (!isMountedRef.current) {
        return;
      }

      setActivities(data.activities || []);
      setTotal(data.total || 0);
    } catch (err) {
      if (!isMountedRef.current) return;

      setError(err.message || 'Failed to fetch activities');
      setActivities([]);
      setTotal(0);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [orgId, filters.category, filters.userId, filters.entityType, filters.entityId, filters.limit, filters.offset, getAccessTokenSilently]);

  // Track component mount status - separate from fetch dependencies
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []); // Only on mount/unmount

  // Fetch activities when dependencies change
  useEffect(() => {
    if (autoFetch) {
      fetchActivities();
    }
  }, [fetchActivities, autoFetch]);

  return {
    activities,
    total,
    loading,
    error,
    refetch: fetchActivities
  };
};

/**
 * Custom hook to fetch recent activities for an organization
 * @param {string} orgId - Organization ID
 * @param {number} limit - Number of activities to fetch (default: 50)
 * @param {boolean} autoFetch - Whether to auto-fetch on mount (default: true)
 * @returns {Object} - { activities, loading, error, refetch }
 */
export const useRecentActivities = (orgId, limit = 50, autoFetch = true) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(autoFetch);
  const [error, setError] = useState(null);
  const { getAccessTokenSilently } = useAuth0();

  const isMountedRef = useRef(true);

  const fetchActivities = useCallback(async () => {
    if (!orgId) {
      setError('Organization ID is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get Auth0 token
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${orgId}/activities/recent?limit=${limit}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch activities');
      }

      const data = await response.json();

      if (!isMountedRef.current) return;

      setActivities(data.activities || []);
    } catch (err) {
      if (!isMountedRef.current) return;

      setError(err.message || 'Failed to fetch activities');
      setActivities([]);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [orgId, limit, getAccessTokenSilently]);

  // Track component mount status - separate from fetch dependencies
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []); // Only on mount/unmount

  // Fetch activities when dependencies change
  useEffect(() => {
    if (autoFetch) {
      fetchActivities();
    }
  }, [fetchActivities, autoFetch]);

  return {
    activities,
    loading,
    error,
    refetch: fetchActivities
  };
};

/**
 * Custom hook to fetch activities for a specific entity
 * @param {string} orgId - Organization ID
 * @param {string} entityType - Entity type (e.g., "DRONE", "BATTERY")
 * @param {string|number} entityId - Entity ID
 * @param {boolean} autoFetch - Whether to auto-fetch on mount (default: true)
 * @returns {Object} - { activities, total, loading, error, refetch }
 */
export const useEntityActivities = (orgId, entityType, entityId, autoFetch = true) => {
  const [activities, setActivities] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(autoFetch);
  const [error, setError] = useState(null);
  const { getAccessTokenSilently } = useAuth0();

  const isMountedRef = useRef(true);

  const fetchActivities = useCallback(async () => {
    if (!orgId || !entityType || !entityId) {
      setError('Organization ID, entity type, and entity ID are required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get Auth0 token
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${orgId}/activities/entity/${entityType}/${entityId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch entity activities');
      }

      const data = await response.json();

      if (!isMountedRef.current) return;

      setActivities(data.activities || []);
      setTotal(data.total || 0);
    } catch (err) {
      if (!isMountedRef.current) return;

      setError(err.message || 'Failed to fetch entity activities');
      setActivities([]);
      setTotal(0);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [orgId, entityType, entityId, getAccessTokenSilently]);

  // Track component mount status - separate from fetch dependencies
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []); // Only on mount/unmount

  // Fetch activities when dependencies change
  useEffect(() => {
    if (autoFetch) {
      fetchActivities();
    }
  }, [fetchActivities, autoFetch]);

  return {
    activities,
    total,
    loading,
    error,
    refetch: fetchActivities
  };
};
